import { BridgeAction } from '@omni/kit/Types';
import { KitButton } from '@omni/kit/components';
import { IconName } from '@omni/kit/components/KitIcon';
import { useScreenContext } from '@omni/kit/contexts/ScreenContext';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { getSpacing } from '@omni/kit/theming/SpacingType';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';

import { useBlockPageContext } from '../BlockPageContext';
import { IBlockProps } from './types';

const debug = require('debug')('tca:blocks:blocks:ButtonBlock');

// These props are publicly available
export interface IButtonBlockProps extends IBlockProps {
  align?: 'start' | 'end' | 'center';
  title?: string;
  /** An action that will occur when a user taps/clicks on the block. */
  action?: BridgeAction | null;
  secondary?: boolean;
  color?: string;
  textColor?: string;
  width?: 'full' | 'content';
  icon?: IconName;
}

export default function ButtonBlock(props: IButtonBlockProps): ReactElement {
  const {
    align,
    title = 'New Button',
    insetStyle = {},
    topSpacing = null,
    bottomSpacing = null,
    horizontalSpacing,
    secondary = true,
    color,
    textColor,
    width,
    icon,
  } = props;

  const { dispatchEvent, tokens } = useShellContext();

  const { edgeSpacing } = useScreenContext({
    fixedSpacingType: horizontalSpacing,
  });

  const [isLoading, setIsLoading] = useState(false);

  const { action } = props;

  const { dispatchAction } = useBlockPageContext();

  const isAuth = action?.handler === 'auth' && action.hasOwnProperty('appKey');
  const isSmall = width === 'content';

  const userToken = useMemo(() => tokens.user, [tokens.user]);

  /**
   * DISP-5888: When a user finishes logging in, the app will receive
   * a new token, then Block Page will reload itself.
   * The login button should show a loading state until the page is reloaded
   * so that a user will be aware that the Block Page reload is in progress.
   * Otherwise the user may think that login is not working and can lead to
   * app rejections by Apple's review team.
   */
  useEffect(() => {
    if (isAuth && userToken) {
      setIsLoading(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  const _onPress = async () => {
    if (isAuth) {
      dispatchEvent({
        type: 'AUTHENTICATE_USER',
        openInNewTab: true,
      });
    } else {
      setIsLoading(true);
      await dispatchAction(action);
      setIsLoading(false);
    }
  };

  const viewStyle = {
    marginTop: getSpacing(topSpacing),
    marginBottom: getSpacing(bottomSpacing),
    ...insetStyle,
  };

  const getAlignmentStyle = () => {
    if (!isSmall) return {};

    if (!align || align === 'center') return { alignItems: 'center' as const };

    if (align === 'start') return { alignItems: 'flex-start' as const };

    if (align === 'end') return { alignItems: 'flex-end' as const };

    return {};
  };

  return (
    <>
      <View style={viewStyle}>
        <View style={getAlignmentStyle()}>
          <View
            style={{
              marginHorizontal: edgeSpacing,
            }}
          >
            <KitButton
              title={title}
              secondary={secondary}
              onPress={_onPress}
              color={color}
              textColor={textColor}
              small={isSmall}
              icon={icon}
              isLoading={isLoading}
            />
          </View>
        </View>
      </View>
    </>
  );
}
